import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import ReactTooltip from "react-tooltip";
import useOrientationJobList from "../OrientationJobList/useOrientationJobList";
import useStore from "../../../context/useStore";
import tools from "../../../helpers/tools";
import dataService from "../../../helpers/dataService";

const SynchroFTStatus = ({ categorie, entiteId }) => {
  const [state] = useStore();
  const { retryJob, retriedJobs } = useOrientationJobList(false);
  const [syncData, setSyncData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const canViewSyncButton = tools.canViewFTSync(state.auth?.user);

  useEffect(() => {
    if (!categorie || !entiteId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    dataService.get(
      `france-travail/${categorie}/${entiteId}/synchronisations/derniere`,
      (data) => setSyncData(data),
      () => setSyncData(null),
      () => setIsLoading(false),
    );
  }, [categorie, entiteId]);

  if (isLoading) {
    return (
      <span className="badge badge-light">
        <i className="fa fa-spinner fa-spin mr-2" />
        Chargement...
      </span>
    );
  }

  // Cas: aucune synchro pour cette entité (API renvoie null)
  if (!syncData) {
    return (
      <span className="badge badge-secondary">
        <i className="fa fa-times mr-2" />
        Non synchro avec FT
      </span>
    );
  }

  const { synchroniseLe, erreur, statut } = syncData;

  if (!statut && !erreur) {
    return (
      <span className="badge badge-warning">
        <i className="fa fa-warning mr-2" />
        aucun statut et aucune erreur
      </span>
    );
  }

  // Cas PENDING: synchronisation en cours côté API
  if (statut === "PENDING") {
    return (
      <span className="badge badge-warning">
        <i className="fa fa-spinner fa-spin mr-2" />
        Synchronisation en cours
      </span>
    );
  }

  // Cas DONE: synchronisation réussie
  if (statut === "DONE") {
    return (
      <span className="badge badge-success">
        <i className="fa fa-link mr-2" />
        Synchro FT OK le{" "}
        {DateTime.fromISO(synchroniseLe).toFormat("dd/MM/yyyy à HH:mm")}
      </span>
    );
  }

  // Cas ERROR sans failedJob encore remonté : pas de bouton de relance
  if (statut === "ERROR" && !erreur) {
    return (
      <span className="badge badge-danger">
        <i className="fa fa-exclamation-triangle mr-2" />
        Erreur synchro FT
      </span>
    );
  }

  // Cas ERROR avec failedJob : relance possible
  const isRetrying = retriedJobs.has(erreur.id);
  if (isRetrying) {
    return (
      <span className="badge badge-warning">
        <i className="fa fa-spinner fa-spin mr-2" />
        Synchro relancée...
      </span>
    );
  }

  const errorTooltip = `${erreur.message} (le ${DateTime.fromISO(
    erreur.erreurLe,
  ).toFormat("dd/MM/yyyy à HH:mm")})`;
  const tooltipId = `synchro-error-${erreur.id}`;

  return (
    <>
      <span
        className="badge badge-danger  d-inline-flex align-items-center justify-content-between border border-danger rounded-pill pl-2"
        style={{ minWidth: "fit-content" }}
        data-tip={errorTooltip}
        data-for={tooltipId}
      >
        <span>
          <i className="fa fa-exclamation-triangle mr-2" />
          Erreur synchro FT
        </span>
        {canViewSyncButton && (
          <button
            className="btn btn-sm btn-danger ml-2 py-0 px-2 rounded-pill"
            style={{ lineHeight: "1", minHeight: 0 }}
            onClick={() => retryJob(erreur.id)}
            title="Réessayer"
          >
            <i className="fa fa-sync" />
          </button>
        )}
      </span>
      <ReactTooltip id={tooltipId} place="right" effect="solid" />
    </>
  );
};

export default SynchroFTStatus;
